import React from "react";
import { Link } from "react-router-dom";
import useSeo from "../hooks/useSeo";
import { COMPANY } from "../lib/company";

const SECTIONS = [
  {
    id: "intro",
    title: "1. Generelt",
    body: (
      <>
        <p>
          Disse erhvervsbetingelser ("<b>Betingelserne</b>") gælder mellem
          {" "}<b>{COMPANY.legal_name}</b> (CVR {COMPANY.cvr}, herefter "EMA Kurer", "vi" eller "os")
          {" "}og enhver erhvervskunde ("<b>Kunden</b>"), der bestiller eller anvender EMA Kurers
          {" "}erhvervs-leveringsservices, herunder Same Day, Next Day, aften-, butiks- og webshop-levering på Sjælland.
        </p>
        <p>
          Betingelserne erstatter EMA Kurers private handelsbetingelser for alle B2B-aftaler.
          {" "}Ved enhver afvigelse mellem prisaftalen, individuelle aftalevilkår og disse Betingelser har individuelle aftalevilkår forrang.
        </p>
      </>
    ),
  },
  {
    id: "scope",
    title: "2. Ydelser",
    body: (
      <>
        <p>EMA Kurer leverer følgende erhvervsydelser:</p>
        <ul>
          <li>Same Day og Next Day lokal pakkelevering på Sjælland</li>
          <li>Butiks- og webshoplevering (B2C på vegne af Kunden)</li>
          <li>Aftenlevering 18:00–22:00 (efter aftale)</li>
          <li>B2B-portal med forsendelses-oprettelse, CSV/Excel-import, labels (10×15 og A6), public tracking, pickup-manifests, failed-delivery håndtering, redelivery, returer, faktura-grundlag og API/webhooks</li>
          <li>Driver PWA og chauffør-bekræftelse af afsluttede ruter</li>
        </ul>
      </>
    ),
  },
  {
    id: "agreement",
    title: "3. Aftaleindgåelse og volumenpriser",
    body: (
      <>
        <p>
          Aftale indgås ved Kundens accept af en individuel pristabel (Aftale-PDF udsendt fra EMA Kurer)
          {" "}eller ved Kundens første brug af platformen under den valgte pakke (Pay as you go / Starter / Growth / Scale / Enterprise).
          {" "}Prislisten på {" "}<Link to="/priser" className="text-brand-red underline">/priser</Link> er gældende ved aftaletidspunktet.
        </p>
        <p>
          EMA Kurer kan ændre priser og gebyrer med <b>30 dages skriftligt varsel</b> til Kundens registrerede e-mail.
          {" "}Allerede oprettede forsendelser afregnes efter den pris, der var gældende på oprettelsestidspunktet.
        </p>
      </>
    ),
  },
  {
    id: "delivery",
    title: "4. Levering og leveringstid",
    body: (
      <>
        <p>
          Leveringstider er vejledende og forudsætter, at:
        </p>
        <ul>
          <li>Forsendelsen er klar til afhentning på det aftalte pickup-tidspunkt (cutoff for Same Day er <b>kl. 14:00 KBH-tid</b>; Next Day cutoff er <b>kl. 16:00 KBH-tid</b>)</li>
          <li>Modtagerens adresse er fuldt udfyldt og inden for Sjælland</li>
          <li>Pakkens dimensioner og vægt overholder grænserne (maks. 11 kg pr. kolli, maks. 60×40×40 cm pr. kolli — kontakt os for større forsendelser)</li>
          <li>Modtager kan kontaktes pr. telefon eller e-mail ved leveringsforsøg</li>
        </ul>
        <p>
          EMA Kurer er ikke ansvarlig for forsinkelse forårsaget af force majeure, trafik, vejr, demonstrationer, IT-nedbrud hos tredjepart eller manglende adgang til afhentnings-/leveringsadressen.
        </p>
      </>
    ),
  },
  {
    id: "failed",
    title: "5. Mislykket levering, genlevering og retur",
    body: (
      <>
        <p>
          EMA Kurer forsøger som udgangspunkt levering <b>én gang</b>. Hvis modtageren ikke kan træffes,
          {" "}eller pakken af andre årsager ikke kan afleveres, registreres forsendelsen som "failed_delivery"
          {" "}med årsagskode og — hvor muligt — bevisbillede.
        </p>
        <p>
          <b>Første failed-delivery er gratis</b>, medmindre årsagen er <i>forkert adresse</i>, <i>forkert telefonnummer</i> eller <i>kunde annulleret</i> — disse opkræves uanset.
          {" "}Yderligere forsøg eller genlevering opkræves som angivet i Pristabellen
          {" "}({" "}se <Link to="/priser" className="text-brand-red underline">/priser</Link>).
        </p>
        <p>
          <b>Returregler for ikke-leverede pakker</b> (gælder vores chauffører og evt. underleverandører):
        </p>
        <ul>
          <li>Normal failed-delivery: returneres til EMA Kurer/HUB/Pickup location <b>senest 2 hverdage efter rutedato</b>.</li>
          <li>Afbrudt rute: alle resterende pakker skal returneres <b>samme dag</b>. Pakker må ikke opbevares privat eller blive i bilen uden aftale med EMA Kurer.</li>
        </ul>
      </>
    ),
  },
  {
    id: "fees",
    title: "5a. Tillægsgebyrer og servicegebyrer",
    body: (
      <>
        <p>
          Følgende gebyrer kan opkræves ud over abonnement og forsendelsespris. Alle priser er ekskl. moms og fremgår også på <Link to="/priser" className="text-brand-red underline">/priser</Link>:
        </p>
        <ul>
          <li><b>Mislykket levering:</b> 50 kr — 1. forsøg er gratis medmindre årsagen er forkert adresse/telefon eller kunde-annulleret.</li>
          <li><b>Genlevering (redelivery):</b> 59 kr — pr. nyt leveringsforsøg efter første failed.</li>
          <li><b>Retur til afsender:</b> 59 kr — hvis modtager nægter modtagelse eller pakken ikke kan leveres efter 2 forsøg.</li>
          <li><b>Forkert adresse eller telefon:</b> 50 kr — opkræves altid, da fejlen ligger hos Kunden.</li>
          <li><b>Ekstra stop:</b> 39-59 kr — afhænger af zone og rute-tæthed.</li>
          <li><b>Manuelt label / manuel håndtering:</b> 10-15 kr — pr. forsendelse, hvis Kunden ikke kan levere brugbare label-data.</li>
          <li><b>Ventetid ved afhentning:</b> 75 kr pr. påbegyndt 15 minutters ventetid ud over det aftalte pickup-vindue.</li>
        </ul>
        <p>
          Alle gebyrer specificeres på det månedlige faktura-grundlag i Kundens portal. Kunden kan gøre indsigelse mod et gebyr inden 14 dage efter fakturadato, jf. § 7.
        </p>
      </>
    ),
  },
  {
    id: "pilot",
    title: "5b. Pilot-vilkår (først 3 måneder)",
    body: (
      <>
        <p>
          Hvis Kunden indgår aftalen som <b>pilot-kunde</b>, gælder følgende særvilkår de første 3 måneder eller indtil pilotperioden afsluttes:
        </p>
        <ul>
          <li><b>Volumen-rabat:</b> 15-25% rabat på pris pr. forsendelse ift. standard-pakken — fremgår af den individuelle pilot-pristabel.</li>
          <li><b>Gratis setup:</b> Ingen onboarding-fee under pilot.</li>
          <li><b>Forventet volumen:</b> Kunden tilstræber minimum 50 forsendelser/uge for at give meningsfulde driftsdata.</li>
          <li><b>Manuel fallback:</b> Hvis portalen eller API'en svigter, kan Kunden sende CSV på e-mail til {COMPANY.email_general} → vi importerer manuelt inden for 2 arbejdstimer.</li>
          <li><b>Daglig review:</b> Vi gennemgår dagens leveringer i Control Tower hver morgen kl. 07:00 (manuelt under pilot).</li>
          <li><b>Dedikeret kontaktperson:</b> Kunden får én fast EMA-kontaktperson under hele pilotperioden + ugentligt 30 min review-møde.</li>
          <li><b>Exit-klausul:</b> Begge parter kan opsige pilotaftalen med <b>14 dages skriftligt varsel</b> uden begrundelse.</li>
          <li><b>SLA under pilot:</b> Vi tilstræber 95% on-time levering (vs. 99% i fuld drift). Servicekredits aktiveres først efter pilotperiodens udløb.</li>
          <li><b>Forbudte forsendelser:</b> Ingen high-risk varer, medicin/temperatur-følsomt gods eller ADR-gods kan sendes under pilot uden separat skriftlig aftale.</li>
        </ul>
        <p>
          Efter pilotperioden konverteres aftalen automatisk til standard-vilkår fra § 1-12, medmindre parterne aftaler andet skriftligt mindst 14 dage inden pilotens udløb.
        </p>
      </>
    ),
  },
  {
    id: "force-majeure",
    title: "5c. Force majeure",
    body: (
      <>
        <p>
          EMA Kurer er ikke ansvarlig for forsinkelser eller manglende levering forårsaget af forhold uden for EMA Kurers rimelige kontrol, herunder:
        </p>
        <ul>
          <li>Ekstreme vejrforhold (snestorm, oversvømmelse, isglatte veje)</li>
          <li>Naturkatastrofer, brand, eksplosion</li>
          <li>Strejker, lockouts og arbejdsmarkedskonflikter</li>
          <li>Trafikulykker, lukkede veje, demonstrationer</li>
          <li>IT-nedbrud hos tredjeparter (Stripe, Billy, ORS, MongoDB Atlas, Twilio m.v.)</li>
          <li>Krig, terror, civile uroligheder</li>
          <li>Myndighedsforanstaltninger (politi-spærringer, lockdowns, karantæne)</li>
          <li>Pandemier og epidemier</li>
        </ul>
        <p>
          Ved force majeure suspenderes EMA Kurers leveringsforpligtelse i den periode, force majeure varer. EMA Kurer informerer Kunden hurtigst muligt og tilbyder ny leveringsdato uden ekstra omkostning. Aflysning på grund af force majeure udløser ikke refusion eller erstatningskrav.
        </p>
      </>
    ),
  },
  {
    id: "obligations",
    title: "6. Kundens forpligtelser",
    body: (
      <>
        <p>Kunden er ansvarlig for at:</p>
        <ul>
          <li>Sikre korrekte modtager-oplysninger (navn, adresse, postnr., by, telefon, e-mail)</li>
          <li>Pakke forsendelser forsvarligt og angive korrekt vægt + dimensioner</li>
          <li>Mærke evt. skrøbeligt, varme/kølevarer, batterier eller andre særkrav</li>
          <li>Ikke sende forbudte varer (våben, narkotika, levende dyr, eksplosiver, kontanter over 10.000 kr., menneskerester m.m.)</li>
          <li>Beskytte sine API-nøgler og portal-login</li>
        </ul>
      </>
    ),
  },
  {
    id: "invoicing",
    title: "7. Fakturering og betaling",
    body: (
      <>
        <p>
          EMA Kurer fakturerer Kunden <b>månedligt bagud</b> via vores Billy-integration.
          {" "}Betalingsfrist: <b>14 dage netto</b> fra fakturadato.
        </p>
        <p>
          Faktura-grundlag genereres automatisk i Kundens portal under "Faktura-grundlag" og kan downloades som PDF/Excel/CSV.
          {" "}Indsigelser mod en faktura skal rejses skriftligt senest <b>14 dage efter fakturadato</b> — herefter anses fakturaen for godkendt.
        </p>
        <p>
          Ved overskridelse af betalingsfristen kan EMA Kurer opkræve <b>renter på 1,5% pr. påbegyndt måned</b>
          {" "}samt rykkergebyr efter rentelovens regler. Vedvarende manglende betaling kan medføre suspension af
          {" "}leverancer og adgang til portalen.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "8. Erstatningsansvar og forsikring",
    body: (
      <>
        <p>
          EMA Kurers samlede erstatningsansvar er begrænset til:
        </p>
        <ul>
          <li>Den faktiske dokumenterede skade på den enkelte forsendelse, dog maks. <b>2.000 DKK pr. forsendelse</b> (medmindre højere forsikring er aftalt skriftligt mod tillægspris)</li>
          <li>For dokumenter, glas, elektronik og andre særligt skrøbelige varer kun hvis disse er mærket korrekt + aftalt skriftligt</li>
        </ul>
        <p>
          EMA Kurer er ikke ansvarlig for indirekte tab (driftstab, avancetab, forventet omsætning, image-skade, dagbøder eller lignende).
        </p>
        <p>
          Eventuelt erstatningskrav skal anmeldes skriftligt til <a href={`mailto:${COMPANY.email_general}`} className="text-brand-red underline">{COMPANY.email_general}</a> senest <b>7 dage</b> efter Kundens kendskab til skaden.
        </p>
      </>
    ),
  },
  {
    id: "gdpr",
    title: "9. Databehandling og GDPR",
    body: (
      <>
        <p>
          Ved levering på vegne af Kunden behandler EMA Kurer modtagerdata (navn, adresse, telefon, e-mail, GPS-positioner, POD) som <b>databehandler</b> for Kunden (data-controller).
          {" "}En særskilt databehandleraftale (DPA) kan indgås på Kundens anmodning. Indtil DPA er underskrevet gælder disse Betingelser samt vores
          {" "}<Link to="/privatlivspolitik" className="text-brand-red underline">privatlivspolitik</Link> som behandlingsgrundlag.
        </p>
        <p>
          EMA Kurer benytter følgende databehandlere: Stripe (betaling), Billy (fakturering), Simply.com (e-mail), MongoDB Atlas (hosting i EU), Twilio (SMS — kun ved aktivering), OpenRouteService (rute-beregning).
        </p>
      </>
    ),
  },
  {
    id: "sla",
    title: "10. Service Level Agreement (SLA)",
    body: (
      <>
        <p>For Scale- og Enterprise-pakker tilbyder EMA Kurer følgende SLA:</p>
        <ul>
          <li><b>Same Day on-time rate:</b> ≥95% leveret inden cutoff på samme dag</li>
          <li><b>Platform uptime:</b> ≥99% (B2B-portal + API)</li>
          <li><b>Support response:</b> Inden for 1 arbejdstime (man-fre 08-18)</li>
          <li><b>Kvartalsvis SLA-rapport</b> med leveringsstatistik, failure-rate og uptime</li>
        </ul>
        <p>
          Manglende SLA-opfyldelse over et kvartal udløser servicekredit på <b>5% af månedens abonnement</b> pr. afvigelse — efter anmodning fra Kunden inden 30 dage.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    title: "11. Opsigelse og ophør",
    body: (
      <>
        <p>
          Aftalen kan opsiges af begge parter med <b>en kalendermåneds varsel</b> til udgangen af en måned.
          {" "}Enterprise- og custom-aftaler kan have længere binding/varsel — fremgår af den individuelle aftale.
        </p>
        <p>
          Ved opsigelse fortsætter Kunden med at hæfte for allerede afsendte forsendelser, gebyrer og månedsabonnement frem til ophørsdatoen.
          {" "}Kundens data eksporteres på anmodning, hvorefter persondata slettes efter den lovbestemte opbevaringsfrist (5 år for regnskabsdata, jf. bogføringsloven).
        </p>
        <p>
          EMA Kurer kan ophæve aftalen uden varsel ved Kundens væsentlige misligholdelse — herunder betalingsstandsning, konkurs, gentagne forbudte forsendelser eller misbrug af platformen.
        </p>
      </>
    ),
  },
  {
    id: "law",
    title: "12. Lovvalg og værneting",
    body: (
      <>
        <p>
          Disse Betingelser er underlagt dansk ret. Eventuelle tvister afgøres ved EMA Kurers værneting (Retten i Roskilde) som første instans.
          {" "}Parterne forpligter sig dog til først at forsøge en mindelig løsning via dialog inden retssag indledes.
        </p>
        <p className="text-brand-text2 text-sm mt-6 italic">
          Disse betingelser er ikke juridisk rådgivning. De skal gennemses af en dansk advokat inden fuld kommerciel lancering.
        </p>
      </>
    ),
  },
];

export default function BusinessTermsPage() {
  useSeo({
    title: "Erhvervsbetingelser · EMA Kurer ApS",
    description:
      "Erhvervsbetingelser for B2B-kunder hos EMA Kurer ApS — Same Day og Next Day pakkelevering på Sjælland. Fakturering, SLA, ansvar, GDPR og opsigelse.",
    path: "/erhvervsbetingelser",
  });

  return (
    <div className="bg-brand-bg2 min-h-screen" data-testid="business-terms-page">
      <section className="bg-brand-dark text-white py-14 lg:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Juridisk · B2B</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight mt-3">
            Erhvervsbetingelser
          </h1>
          <p className="text-white/70 mt-5 max-w-2xl">
            Gælder for alle erhvervskunder, der bestiller eller anvender EMA Kurer ApS' B2B-leveringsservices på Sjælland.
            Senest opdateret: {new Date().toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" })}.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-16">
        <div className="bg-white border border-brand p-6 sm:p-10 prose prose-sm max-w-none">
          <p className="text-sm text-brand-text2 italic mb-8 border-l-4 border-brand-red pl-4">
            <b>Disclaimer:</b> Disse betingelser er ikke juridisk rådgivning. De skal gennemses af en dansk advokat inden fuld kommerciel lancering. Kontakt {COMPANY.email_general} ved spørgsmål.
          </p>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="mb-10" data-testid={`business-terms-section-${s.id}`}>
              <h2 className="font-display font-semibold text-2xl text-brand-dark mt-0">
                {s.title}
              </h2>
              <div className="text-brand-text2 text-sm leading-relaxed space-y-3 mt-3 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-1 [&_b]:text-brand-dark">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 p-6 bg-white border border-brand text-sm">
          <div className="kicker mb-3">Kontakt</div>
          <p className="text-brand-text2">
            {COMPANY.legal_name} · CVR {COMPANY.cvr}<br/>
            Generel: <a href={`mailto:${COMPANY.email_general}`} className="text-brand-red hover:underline">{COMPANY.email_general}</a><br/>
            Erhvervs-forespørgsler: <a href={`mailto:${COMPANY.email_business}`} className="text-brand-red hover:underline">{COMPANY.email_business}</a><br/>
            Bogholderi: <a href={`mailto:${COMPANY.email_billing}`} className="text-brand-red hover:underline">{COMPANY.email_billing}</a><br/>
            Telefon: <a href={`tel:${COMPANY.phone_e164}`} className="text-brand-red hover:underline">{COMPANY.phone}</a>
          </p>
        </div>
      </section>
    </div>
  );
}
